import { FC, useCallback } from 'react'
import {
    Container,
    InnerContainer,
    ReviewWrapper,
    ContentContainer,
    RatingWrapper,
    RatingStarContainer,
    RatingInnerContainer,
    StarIconContainer,
    IconContainer,
    RatingContainer,
    VillaIcon,
    WalletIcon,
    SmileIcon,
    MapPinIcon,
    StarIcon,
    DottedLine,
    ButtonsWrapper,
    RatingSourceType,
    GoogleIcon,
} from './styles'
import { useTranslation } from '../../../../hooks/locale'
import { Typography } from 'react-components'
import { FormattedRating, RatingSourceTypeEnum } from '../../../../models/property-details-page/reviews-model'
import usePagination from '../../../../hooks/pagination'
import { StyledGhostButton, CTAButton } from '../../styles'
import AnalyticsService from '../../../../services/analytics-service'
import { pdpPageActions } from '../../../../services/analytics-service/actions'
import ReviewAnswer from './review-answer/index'
import { usePropertyDetailsContext } from '../../../../hooks/property-details'
import { getCategory } from '../../../../utils/category-utils'

interface ReviewSectionProps {
    reviews: FormattedRating[]
    replyTitle: string
}

const RATINGS_TO_DISPLAY = 2

const ReviewSection: FC<ReviewSectionProps> = ({ reviews, replyTitle }) => {
    const { t } = useTranslation()
    const { isComboListing } = usePropertyDetailsContext()

    const ratingsData = reviews.map(review => [
        { Icon: StarIcon, rating: review.avg, isStar: true },
        { Icon: VillaIcon, rating: review.features },
        { Icon: WalletIcon, rating: review.price },
        { Icon: SmileIcon, rating: review.service },
        { Icon: MapPinIcon, rating: review.ambience },
    ])

    const { cumulativeData, hasNextPage, hasPreviousPage, goToFirstPage, nextPage } = usePagination(
        reviews,
        RATINGS_TO_DISPLAY,
    )

    const goToNextPage = useCallback(() => {
        AnalyticsService.trackEvent({
            category: getCategory(isComboListing),
            action: pdpPageActions.SHOW_MORE_REVIEWS_BUTTON_CLICKED,
        })
        nextPage()
    }, [nextPage, isComboListing])

    const goToInitialState = useCallback(() => {
        AnalyticsService.trackEvent({
            category: getCategory(isComboListing),
            action: pdpPageActions.HIDE_REVIEWS_CLICKED,
        })
        goToFirstPage()
    }, [goToFirstPage, isComboListing])

    return (
        <Container>
            <InnerContainer>
                {cumulativeData.map((review, idx) => {
                    const filteredAnswers = review.answers?.filter(answer => !!answer.text) ?? []
                    return (
                        <ReviewWrapper key={`${review.travelDate}_${idx}`}>
                            <ContentContainer>
                                <Typography variant="subtitle1" fontWeight={'bold'}>
                                    {review.title}
                                </Typography>
                                <Typography variant="caption" color="tfwBlack40" fontWeight="semiBold">
                                    {t('propertyRatings.reviewerDetials', {
                                        type: review.touristType || '',
                                        lastName: review.contact.lastName || '',
                                        city: review.contact.address.city,
                                        reviewDate: review.ratingDate || '',
                                    })}
                                </Typography>
                            </ContentContainer>

                            <ContentContainer>
                                {review.travelDate && (
                                    <Typography variant="subtitle2" fontWeight={'semiBold'}>{`${t('travelPeriod')}: ${
                                        review.travelDate
                                    }`}</Typography>
                                )}
                                <Typography variant="caption" fontWeight="semiBold" color="tfwBlack40">
                                    {`${t('travellingAs')}: ${review.touristType}`}{' '}
                                </Typography>
                            </ContentContainer>

                            <RatingWrapper>
                                {ratingsData[idx].map((data, index) =>
                                    data.isStar ? (
                                        <RatingStarContainer key={index}>
                                            <StarIconContainer>
                                                <data.Icon />
                                            </StarIconContainer>
                                            <RatingContainer>
                                                <Typography variant="subtitle2" fontWeight={'semiBold'}>
                                                    {data.rating}
                                                </Typography>
                                            </RatingContainer>
                                        </RatingStarContainer>
                                    ) : (
                                        <RatingInnerContainer key={index}>
                                            <IconContainer>
                                                <data.Icon />
                                            </IconContainer>
                                            <RatingContainer>
                                                <Typography variant="subtitle2" fontWeight={'semiBold'}>
                                                    {data.rating}
                                                </Typography>
                                            </RatingContainer>
                                        </RatingInnerContainer>
                                    ),
                                )}
                            </RatingWrapper>

                            <ContentContainer>
                                <Typography variant="subtitle2" fontWeight="regular">
                                    {review.text}
                                </Typography>
                            </ContentContainer>

                            {filteredAnswers.length > 0 &&
                                filteredAnswers.map((answer, index) => (
                                    <ReviewAnswer
                                        key={`${review.travelDate}_${answer.date}_${index}`}
                                        answer={answer}
                                        replyTitle={replyTitle}
                                    />
                                ))}

                            {review.ratingSourceType === RatingSourceTypeEnum.GOOGLE_REVIEWS && (
                                <RatingSourceType>
                                    <Typography variant="caption" color="tfwBlack40">
                                        {t('propertyRatings.ratingSourceType')}
                                    </Typography>
                                    <GoogleIcon />
                                </RatingSourceType>
                            )}
                            {idx < cumulativeData.length - 1 && <DottedLine />}
                        </ReviewWrapper>
                    )
                })}
            </InnerContainer>
            <ButtonsWrapper>
                {hasPreviousPage && (
                    <CTAButton onClick={goToInitialState}>
                        <StyledGhostButton isPrimary={false}>
                            <Typography variant="subtitle2" fontWeight="regular">
                                {t(`propertyRatings.hideRatings`)}
                            </Typography>
                        </StyledGhostButton>
                    </CTAButton>
                )}
                {hasNextPage && (
                    <CTAButton onClick={goToNextPage}>
                        <StyledGhostButton isPrimary={false}>
                            <Typography variant="subtitle2" fontWeight="regular">
                                {t(`propertyRatings.showMoreRatings`)}
                            </Typography>
                        </StyledGhostButton>
                    </CTAButton>
                )}
            </ButtonsWrapper>
        </Container>
    )
}

export default ReviewSection
